import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from '../components/ui/command';
import { Users, ShoppingCart, CurrencyInr, Receipt, MagnifyingGlass } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const UniversalSearch = ({ open, onOpenChange, onNavigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ clients: [], sales: [], purchases: [], expenses: [] });
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults({ clients: [], sales: [], purchases: [], expenses: [] });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/search`, {
        params: { q, limit: 6 },
        withCredentials: true,
      });
      setResults(data);
    } catch (err) { console.error(err.message);
      setResults({ clients: [], sales: [], purchases: [], expenses: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, search]);

  useEffect(() => {
    if (!open) { setQuery(''); setResults({ clients: [], sales: [], purchases: [], expenses: [] }); }
  }, [open]);

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  const hasResults = results.clients.length > 0 || results.sales.length > 0 || results.purchases.length > 0 || results.expenses.length > 0;

  const handleSelect = (type, item) => {
    onOpenChange(false);
    onNavigate?.(type, item);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <CommandInput
        placeholder="Search clients, sales, purchases, expenses..."
        value={query}
        onValueChange={setQuery}
        data-testid="universal-search-input"
      />
      <CommandList className="max-h-[400px]" data-testid="universal-search-results">
        {query.length < 2 && (
          <div className="py-8 text-center">
            <MagnifyingGlass size={32} weight="thin" className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Type at least 2 characters to search across the entire app</p>
            <p className="text-xs text-muted-foreground mt-1">Search clients, sales, purchases, and expenses</p>
          </div>
        )}

        {query.length >= 2 && !loading && !hasResults && (
          <CommandEmpty>No results found for "{query}"</CommandEmpty>
        )}

        {loading && query.length >= 2 && (
          <div className="py-6 text-center text-sm text-muted-foreground">Searching...</div>
        )}

        {results.clients.length > 0 && (
          <CommandGroup heading="Clients">
            {results.clients.map(c => (
              <CommandItem
                key={`client-${c.id}`}
                onSelect={() => handleSelect('client', c)}
                className="cursor-pointer"
                data-testid={`search-result-client-${c.id}`}
              >
                <Users size={16} weight="regular" className="mr-2 text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{c.name}</span>
                  {c.company && <span className="text-muted-foreground ml-2 text-xs">({c.company})</span>}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.clients.length > 0 && (results.sales.length > 0 || results.purchases.length > 0 || results.expenses.length > 0) && (
          <CommandSeparator />
        )}

        {results.sales.length > 0 && (
          <CommandGroup heading="Sales">
            {results.sales.map(s => (
              <CommandItem
                key={`sale-${s.id}`}
                onSelect={() => handleSelect('sale', s)}
                className="cursor-pointer"
                data-testid={`search-result-sale-${s.id}`}
              >
                <CurrencyInr size={16} weight="regular" className="mr-2 text-green-500 shrink-0" />
                <div className="flex-1 min-w-0 flex items-center justify-between">
                  <div>
                    <span className="font-medium">{s.client_name}</span>
                    {s.invoice_number && <span className="text-muted-foreground ml-2 text-xs">#{s.invoice_number}</span>}
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <span className="text-sm font-semibold text-green-600 dark:text-green-400">₹{s.amount?.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground ml-2">{s.date?.slice(0, 10)}</span>
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.sales.length > 0 && (results.purchases.length > 0 || results.expenses.length > 0) && (
          <CommandSeparator />
        )}

        {results.purchases.length > 0 && (
          <CommandGroup heading="Purchases">
            {results.purchases.map(p => (
              <CommandItem
                key={`purchase-${p.id}`}
                onSelect={() => handleSelect('purchase', p)}
                className="cursor-pointer"
                data-testid={`search-result-purchase-${p.id}`}
              >
                <ShoppingCart size={16} weight="regular" className="mr-2 text-orange-500 shrink-0" />
                <div className="flex-1 min-w-0 flex items-center justify-between">
                  <div>
                    <span className="font-medium">{p.supplier}</span>
                    {p.invoice_number && <span className="text-muted-foreground ml-2 text-xs">#{p.invoice_number}</span>}
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">₹{p.amount?.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground ml-2">{p.date?.slice(0, 10)}</span>
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.purchases.length > 0 && results.expenses.length > 0 && (
          <CommandSeparator />
        )}

        {results.expenses.length > 0 && (
          <CommandGroup heading="Expenses">
            {results.expenses.map(e => (
              <CommandItem
                key={`expense-${e.id}`}
                onSelect={() => handleSelect('expense', e)}
                className="cursor-pointer"
                data-testid={`search-result-expense-${e.id}`}
              >
                <Receipt size={16} weight="regular" className="mr-2 text-red-500 shrink-0" />
                <div className="flex-1 min-w-0 flex items-center justify-between">
                  <div>
                    <span className="font-medium">{e.category}</span>
                    {e.description && <span className="text-muted-foreground ml-2 text-xs truncate">{e.description.slice(0, 30)}</span>}
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <span className="text-sm font-semibold text-red-600 dark:text-red-400">₹{e.amount?.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground ml-2">{e.date?.slice(0, 10)}</span>
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default UniversalSearch;
